import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Shield, GraduationCap, UserCircle, Trash2 } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

const ROLE_BADGE = {
  admin: 'bg-primary/10 text-primary border-primary/20',
  teacher: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  parent: 'bg-green-500/10 text-green-500 border-green-500/20',
};

const ROLE_ICON = {
  admin: Shield,
  teacher: GraduationCap,
  parent: UserCircle,
};

export const UserCard = ({ user, isCurrentUser, onRoleChange, onDeleteRequest }) => {
  const { t } = useLanguage();
  const RoleIcon = ROLE_ICON[user.role] || UserCircle;

  return (
    <Card className={`bg-card border-border${isCurrentUser ? ' ring-1 ring-primary' : ''}`}>
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center overflow-hidden shrink-0">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{user.name}</h3>
                {isCurrentUser && <Badge variant="outline" className="text-xs">{t('users.you')}</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Badge className={ROLE_BADGE[user.role] || ROLE_BADGE.parent}>
              <RoleIcon className="w-3 h-3 mr-1" />
              {user.role}
            </Badge>

            <Select
              value={user.role}
              onValueChange={(value) => onRoleChange(user.user_id, value)}
              disabled={isCurrentUser}
            >
              <SelectTrigger className="w-[130px] bg-background border-border" data-testid={`role-select-${user.user_id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="admin"><div className="flex items-center gap-2"><Shield className="w-4 h-4" />{t('users.roleAdmin')}</div></SelectItem>
                <SelectItem value="teacher"><div className="flex items-center gap-2"><GraduationCap className="w-4 h-4" />{t('users.roleTeacher')}</div></SelectItem>
                <SelectItem value="parent"><div className="flex items-center gap-2"><UserCircle className="w-4 h-4" />{t('users.roleParent')}</div></SelectItem>
              </SelectContent>
            </Select>

            {!isCurrentUser && (
              <Button
                variant="ghost" size="icon"
                onClick={() => onDeleteRequest(user)}
                className="text-muted-foreground hover:text-destructive"
                data-testid={`delete-user-${user.user_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
